import React from 'react';
import I18n from 'i18n-js';
import moment from 'moment';
import { useInView } from 'react-intersection-observer';
import {
  BellIcon,
  ChartLineIcon,
  ClockIcon,
  HeartStraightIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PackageIcon,
  PencilSimpleIcon,
  ShoppingCartIcon,
  StarIcon,
  TagIcon,
} from '@phosphor-icons/react';
import { RCIcon, RCText } from '@reverbdotcom/cadence/components';
import { ReverbLogoCircleIcon } from '@reverbdotcom/cadence/icons/react';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { useIntersectionTracking } from '@reverbdotcom/commons/src/use_tracking';
import { NotificationsCenterQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { clickedNotificationEvent, viewedNotificationEvent } from './notification_event_props';
import { useMarkAllStreamItemsRead, useMarkStreamItemRead } from './notifications_read_state';

type NotificationStreamItem = NotificationsCenterQuery['notificationStream']['items'][number];

const STREAM_ICONS: Record<string, JSX.Element> = {
  tag: <TagIcon />,
  'shopping-cart': <ShoppingCartIcon />,
  heart: <HeartStraightIcon />,
  clock: <ClockIcon />,
  package: <PackageIcon />,
  'chart-line': <ChartLineIcon />,
  'map-pin': <MapPinIcon />,
  'magnifying-glass': <MagnifyingGlassIcon />,
  'pencil-simple': <PencilSimpleIcon />,
  star: <StarIcon />,
  'reverb-logo-circle': <ReverbLogoCircleIcon />,
};

function streamIcon(iconName?: string | null): JSX.Element {
  if (!iconName) { return <BellIcon />; }

  return STREAM_ICONS[iconName] ?? <BellIcon />;
}

interface IProps {
  items: NotificationStreamItem[];
  totalUnread: number;
  hasMore: boolean;
  onLoadMore: () => void;
}

export default function StreamSection({ items, totalUnread, hasMore, onLoadMore }: IProps) {
  const markItemRead = useMarkStreamItemRead();
  const markAllRead = useMarkAllStreamItemsRead();

  if (items.length === 0) return null;

  return (
    <div className="notifications-center__stream">
      {/* Static, and deliberately so. It labels the section rather than a run of items, so
          nothing about it depends on read state and items never move between groups. */}
      <div className="notifications-center__stream__heading">
        <RCText.Eyebrow size="300" color="tertiary" htmlTag="span">
          {I18n.t('discovery.notificationsCenter.stream.updates')}
        </RCText.Eyebrow>
        {totalUnread > 0 && (
          <MarkAllAsReadButton onClick={() => markAllRead(totalUnread)} />
        )}
      </div>
      {items.map(item => (
        <StreamItem key={item.id} item={item} onMarkRead={markItemRead} />
      ))}
      {hasMore && <StreamScrollSentinel onEnterView={onLoadMore} />}
    </div>
  );
}

export function MarkAllAsReadButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="notifications-center__stream__mark-all"
      onClick={onClick}
    >
      <RCText.Fine size="300" htmlTag="span">
        {I18n.t('discovery.notificationsCenter.stream.markAllAsRead')}
      </RCText.Fine>
    </button>
  );
}

export function StreamItem({ item, onMarkRead }: {
  item: NotificationStreamItem;
  onMarkRead: (item: NotificationStreamItem) => void;
}) {
  // Independent of mark-read: scrolling past counts as a view whether or not the item is
  // ever clicked, and firing it leaves the read state alone.
  const viewedRef = useIntersectionTracking(
    viewedNotificationEvent(item.eventTracking),
    !item.isRead,
    { threshold: 0.5, rootMargin: '0px' },
  );

  // Detached once the item is read rather than left to `shouldTrack`. The hook builds its
  // observer once and the callback keeps the first render's `shouldTrack`, so Mark all as
  // read would otherwise leave every off-screen row still armed to fire on the next scroll.
  const viewRef = item.isRead ? null : viewedRef;

  return (
    <CoreLink
      ref={viewRef}
      to={item.href}
      className="notifications-center__stream__item"
      clickEvent={clickedNotificationEvent(item.eventTracking)}
      onClick={() => onMarkRead(item)}
    >
      <StreamItemMedia item={item} />
      <span className="notifications-center__stream__item__text">
        {item.eyebrow && (
          <RCText.Eyebrow size="300" color="urgent" htmlTag="span">
            {item.eyebrow}
          </RCText.Eyebrow>
        )}
        <RCText.Utility size="350" weight="semibold" htmlTag="span">
          {item.title}
        </RCText.Utility>
        {item.body && (
          <RCText.Default size="350" htmlTag="span">
            {item.body}
          </RCText.Default>
        )}
        <RCText.Fine size="300" color="secondary" htmlTag="span">
          {moment(item.createdAt).fromNow()}
        </RCText.Fine>
      </span>
      {!item.isRead && (
        <span className="notifications-center__stream__item__unread">
          <span className="visually-hidden">
            {I18n.t('discovery.notificationsCenter.stream.unread')}
          </span>
        </span>
      )}
    </CoreLink>
  );
}

/**
 * Every kind resolves an icon, including the photo-led ones, so an item whose entity has
 * no primary image still shows something sensible rather than an empty frame.
 */
function StreamItemMedia({ item }: { item: NotificationStreamItem }) {
  if (item.thumbnailUrl) {
    return (
      <span className="notifications-center__stream__item__thumbnail">
        <img src={item.thumbnailUrl} alt="" />
      </span>
    );
  }

  return (
    <span className="notifications-center__stream__item__icon">
      <RCIcon icon={streamIcon(item.iconName)} size={7} />
    </span>
  );
}

/**
 * Infinite scroll, no Load More affordance. Watched rather than measured, so the effect
 * re-runs whenever `onEnterView` changes identity - which it does after each page lands.
 * A page short enough to leave the sentinel on screen therefore keeps filling instead of
 * stalling on an intersection that never transitions again.
 */
export function StreamScrollSentinel({ onEnterView }: { onEnterView: () => void }) {
  const { ref, inView } = useInView();

  React.useEffect(() => {
    if (inView) { onEnterView(); }
  }, [inView, onEnterView]);

  return (
    <div
      ref={ref}
      className="notifications-center__stream__sentinel"
      data-testid="notifications-center-stream-sentinel"
      aria-hidden
    />
  );
}
